/**
 * Staging Review Queue Component
 * Shows all staged records pending review across all jobs
 * Allows bulk approval/rejection of records
 * Trust Marketplace - Program Intelligence System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  Database,
  CheckCircle2,
  AlertCircle,
  Clock,
  Eye,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Filter,
  CheckSquare,
  XSquare,
  FileText,
  ExternalLink,
  Calendar,
  MapPin,
  Building2
} from 'lucide-react';

interface StagingRecord {
  id: number;
  job_id: number;
  external_id: string;
  stage: string;
  data_source: string;
  source_name: string;
  validation_passed: boolean;
  validation_errors: string[];
  needs_review: boolean;
  review_reason: string;
  data_quality_score: number;
  confidence_score: number;
  field_completeness: number;
  normalized_data: any;
  raw_data: any;
  created_at: string;
  is_duplicate: boolean;
}

interface StagingStats {
  total: number;
  staged: number;
  transformed: number;
  validated: number;
  loaded: number;
  failed: number;
  pending_review: number;
  duplicates: number;
  by_source: Record<string, number>;
}

interface StagingReviewQueueProps {
  apiBaseUrl: string;
  authToken?: string;
  onRefresh?: () => void;
}

const StagingReviewQueue: React.FC<StagingReviewQueueProps> = ({
  apiBaseUrl,
  authToken,
  onRefresh
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [stats, setStats] = useState<StagingStats | null>(null);
  const [records, setRecords] = useState<StagingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState<StagingRecord | null>(null);
  const [selectedRecords, setSelectedRecords] = useState<Set<number>>(new Set());
  const [filterSource, setFilterSource] = useState<string>('');
  const [filterStage, setFilterStage] = useState<string>('');
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, [filterSource, filterStage, showAllRecords]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load global staging stats
      const statsResponse = await authFetch(
        `${apiBaseUrl}/staging/stats`
      );

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      // Load records - either pending review or all
      const params = new URLSearchParams({
        limit: '100',
        offset: '0'
      });

      if (filterSource) {
        params.append('source', filterSource);
      }
      if (filterStage) {
        params.append('stage', filterStage);
      }
      if (!showAllRecords) {
        params.append('pending_review', 'true');
      }

      const recordsResponse = await authFetch(
        `${apiBaseUrl}/staging/records?${params.toString()}`
      );

      if (recordsResponse.ok) {
        const recordsData = await recordsResponse.json();
        setRecords(recordsData.records || recordsData || []);
      }
    } catch (error) {
      console.error('Error loading staging data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAction = async (recordId: number, action: 'APPROVE' | 'REJECT') => {
    try {
      const response = await authFetch(
        `${apiBaseUrl}/staging/records/${recordId}/review`,
        {
          method: 'POST',
          jsonBody: { action }
        }
      );

      if (response.ok) {
        loadData();
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  const handleBulkAction = async (action: 'APPROVE' | 'REJECT') => {
    if (selectedRecords.size === 0) return;

    setProcessing(true);
    try {
      const promises = Array.from(selectedRecords).map(recordId =>
        authFetch(`${apiBaseUrl}/staging/records/${recordId}/review`, {
          method: 'POST',
          jsonBody: { action }
        })
      );

      await Promise.all(promises);
      setSelectedRecords(new Set());
      loadData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error processing bulk action:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveAll = async () => {
    if (!confirm(`Approve all ${stats?.pending_review || 0} pending records?`)) return;

    setProcessing(true);
    try {
      const response = await authFetch(
        `${apiBaseUrl}/staging/approve-all`,
        {
          method: 'POST',
          jsonBody: {
            source: filterSource || undefined
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(`Approved ${result.approved_count} records`);
        loadData();
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Error approving all:', error);
    } finally {
      setProcessing(false);
    }
  };

  const toggleRecordSelection = (recordId: number) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
  };

  const selectAllVisible = () => {
    const newSelected = new Set(records.map(r => r.id));
    setSelectedRecords(newSelected);
  };

  const clearSelection = () => {
    setSelectedRecords(new Set());
  };

  const getStageColor = (stage: string): string => {
    const colors: Record<string, string> = {
      'STAGED': 'bg-blue-100 text-blue-800',
      'TRANSFORMED': 'bg-purple-100 text-purple-800',
      'VALIDATED': 'bg-yellow-100 text-yellow-800',
      'LOADED': 'bg-green-100 text-green-800',
      'FAILED': 'bg-red-100 text-red-800'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return fmt.dateTime(dateString);
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">{t('admin.loading_staging_data', 'Loading staging data...')}</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Overview */}
      {stats && (
        <Card className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="text-center p-2 bg-blue-50 rounded">
              <div className="text-xl font-semibold text-blue-600">{stats.staged}</div>
              <div className="text-xs text-blue-600">{t('admin.staged', 'Staged')}</div>
            </div>
            <div className="text-center p-2 bg-purple-50 rounded">
              <div className="text-xl font-semibold text-purple-600">{stats.transformed}</div>
              <div className="text-xs text-purple-600">{t('admin.transformed', 'Transformed')}</div>
            </div>
            <div className="text-center p-2 bg-yellow-50 rounded">
              <div className="text-xl font-semibold text-yellow-600">{stats.validated}</div>
              <div className="text-xs text-yellow-600">{t('admin.validated', 'Validated')}</div>
            </div>
            <div className="text-center p-2 bg-green-50 rounded">
              <div className="text-xl font-semibold text-green-600">{stats.loaded}</div>
              <div className="text-xs text-green-600">{t('admin.loaded', 'Loaded')}</div>
            </div>
            <div className="text-center p-2 bg-orange-50 rounded">
              <div className="text-xl font-semibold text-orange-600">{stats.pending_review}</div>
              <div className="text-xs text-orange-600">{t('admin.pending_review', 'Pending Review')}</div>
            </div>
            <div className="text-center p-2 bg-red-50 rounded">
              <div className="text-xl font-semibold text-red-600">{stats.failed}</div>
              <div className="text-xs text-red-600">{t('admin.failed', 'Failed')}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Filters and Actions */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">{t('admin.all_sources', 'All Sources')}</option>
              <option value="SAM_Gov">SAM.gov</option>
              <option value="Grants_Gov">Grants.gov</option>
              <option value="State_Filings">State Filings</option>
              <option value="State_Health_Depts">State Health Depts</option>
              <option value="Medicaid_Waivers">Medicaid Waivers</option>
              <option value="CMS">CMS</option>
              <option value="HRSA">HRSA</option>
            </select>

            <select
              value={filterStage}
              onChange={(e) => setFilterStage(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">{t('admin.all_stages', 'All Stages')}</option>
              <option value="STAGED">Staged</option>
              <option value="TRANSFORMED">Transformed</option>
              <option value="VALIDATED">Validated</option>
              <option value="LOADED">Loaded</option>
              <option value="FAILED">Failed</option>
            </select>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showAllRecords}
                onChange={(e) => setShowAllRecords(e.target.checked)}
                className="rounded"
              />
              {t('admin.show_all_records', 'Show All Records')}
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-1" />
              {t('admin.refresh', 'Refresh')}
            </Button>

            {stats && stats.pending_review > 0 && (
              <Button
                size="sm"
                className="bg-v2accent hover:bg-v2accent/90 text-white"
                onClick={handleApproveAll}
                disabled={processing}
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                {t('admin.approve_all', 'Approve All')} ({stats.pending_review})
              </Button>
            )}
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedRecords.size > 0 && (
          <div className="mt-3 pt-3 border-t flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {selectedRecords.size} selected
            </span>
            <Button
              size="sm"
              className="bg-v2accent hover:bg-v2accent/90 text-white"
              onClick={() => handleBulkAction('APPROVE')}
              disabled={processing}
            >
              <ThumbsUp className="h-3 w-3 mr-1" />
              Approve Selected
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleBulkAction('REJECT')}
              disabled={processing}
            >
              <ThumbsDown className="h-3 w-3 mr-1" />
              Reject Selected
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={clearSelection}
            >
              Clear
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={selectAllVisible}
            >
              Select All
            </Button>
          </div>
        )}
      </Card>

      {/* Records List */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Database className="h-5 w-5" />
            {showAllRecords
              ? t('admin.all_staging_records', 'All Staging Records')
              : t('admin.pending_review_records', 'Pending Review Records')
            }
          </h3>
          <span className="text-sm text-gray-500">{records.length} records</span>
        </div>

        {records.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-400" />
            <p>{t('admin.no_records_pending', 'No records pending review')}</p>
            <p className="text-sm mt-1">{t('admin.all_caught_up', 'All records have been processed')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {records.map(record => (
              <div
                key={record.id}
                className={`border rounded-lg p-4 transition-colors ${
                  selectedRecords.has(record.id) ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedRecords.has(record.id)}
                      onChange={() => toggleRecordSelection(record.id)}
                      className="mt-1 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-lg">
                          {record.normalized_data?.program_name ||
                           record.normalized_data?.title ||
                           record.normalized_data?.opportunity_title ||
                           record.normalized_data?.organization_name ||
                           `Record ${record.id}`}
                        </span>
                        <Badge className={getStageColor(record.stage)}>
                          {record.stage}
                        </Badge>
                        <Badge variant="outline">{record.data_source || record.source_name}</Badge>
                        {record.is_duplicate && (
                          <Badge className="bg-gray-100 text-gray-800">Duplicate</Badge>
                        )}
                        {record.needs_review && (
                          <Badge className="bg-yellow-100 text-yellow-800">Needs Review</Badge>
                        )}
                      </div>

                      {/* Description */}
                      {(record.normalized_data?.description || record.normalized_data?.synopsis) && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {record.normalized_data?.description || record.normalized_data?.synopsis}
                        </p>
                      )}

                      {/* Metadata Row */}
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        {record.normalized_data?.sponsor_name && (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {record.normalized_data.sponsor_name}
                          </span>
                        )}
                        {(record.normalized_data?.state || record.normalized_data?.states) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {record.normalized_data.state || record.normalized_data.states?.join(', ')}
                          </span>
                        )}
                        {record.normalized_data?.deadline && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(record.normalized_data.deadline)}
                          </span>
                        )}
                        {record.normalized_data?.source_url && (
                          <a
                            href={record.normalized_data.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Source
                          </a>
                        )}
                      </div>

                      {/* Quality Scores */}
                      <div className="flex items-center gap-4 mt-2 text-xs">
                        <span className="text-gray-500">
                          Quality: <span className={`font-medium ${
                            record.data_quality_score >= 0.7 ? 'text-green-600' :
                            record.data_quality_score >= 0.5 ? 'text-yellow-600' : 'text-red-600'
                          }`}>{(record.data_quality_score * 100).toFixed(0)}%</span>
                        </span>
                        {record.confidence_score != null && (
                          <span className="text-gray-500">
                            Confidence: <span className="font-medium">{(record.confidence_score * 100).toFixed(0)}%</span>
                          </span>
                        )}
                        <span className="text-gray-500">
                          Created: {formatDate(record.created_at)}
                        </span>
                      </div>

                      {/* Review Reason */}
                      {record.review_reason && (
                        <div className="mt-2 text-sm text-yellow-700 bg-yellow-50 px-2 py-1 rounded flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {record.review_reason}
                        </div>
                      )}

                      {/* Validation Errors */}
                      {record.validation_errors && record.validation_errors.length > 0 && (
                        <div className="mt-2 text-sm text-red-600">
                          Errors: {record.validation_errors.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedRecord(record)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {record.needs_review && (
                      <>
                        <Button
                          size="sm"
                          className="bg-v2accent hover:bg-v2accent/90 text-white"
                          onClick={() => handleReviewAction(record.id, 'APPROVE')}
                        >
                          <ThumbsUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReviewAction(record.id, 'REJECT')}
                        >
                          <ThumbsDown className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Record Detail Modal */}
      <Dialog open={!!selectedRecord} onOpenChange={() => setSelectedRecord(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Record Details
            </DialogTitle>
            <DialogDescription>
              {selectedRecord?.data_source} - {selectedRecord?.stage}
            </DialogDescription>
          </DialogHeader>

          {selectedRecord && (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Summary Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">ID:</span>
                  <span className="ml-2 font-mono">{selectedRecord.id}</span>
                </div>
                <div>
                  <span className="text-gray-500">External ID:</span>
                  <span className="ml-2 font-mono">{selectedRecord.external_id || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Quality Score:</span>
                  <span className="ml-2">{(selectedRecord.data_quality_score * 100).toFixed(1)}%</span>
                </div>
                <div>
                  <span className="text-gray-500">Completeness:</span>
                  <span className="ml-2">{(selectedRecord.field_completeness * 100).toFixed(1)}%</span>
                </div>
              </div>

              {/* Normalized Data */}
              <div>
                <h4 className="font-medium mb-2">Normalized Data</h4>
                <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-3 rounded-lg overflow-auto max-h-64">
                  {JSON.stringify(selectedRecord.normalized_data, null, 2)}
                </pre>
              </div>

              {/* Raw Data (if different) */}
              {selectedRecord.raw_data && (
                <div>
                  <h4 className="font-medium mb-2">Raw Data</h4>
                  <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-3 rounded-lg overflow-auto max-h-64">
                    {JSON.stringify(selectedRecord.raw_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {selectedRecord?.needs_review && (
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                className="bg-v2accent hover:bg-v2accent/90 text-white"
                onClick={() => {
                  handleReviewAction(selectedRecord.id, 'APPROVE');
                  setSelectedRecord(null);
                }}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Approve
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  handleReviewAction(selectedRecord.id, 'REJECT');
                  setSelectedRecord(null);
                }}
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StagingReviewQueue;
